import React from "react";
import { Box, Chip, Divider, IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";

import {
  useLazyGetUniqueDestinationBranchesQuery,
  useLazyNoPriceShipmentListQuery,
} from "store/services/reportApi";
import { useAuth, useTablePagination } from "hooks";
import ExportNoPriceShipmentExcel from "./ExportNoPriceShipmentExcel";
import BaseSelect from "components/Shared/Base/BaseSelect";

const getStatusStyle = (status) => {
  const styles = {
    Pending: { backgroundColor: "#F8C471", textColor: "#D35400" },
    Collected: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
    Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Unloaded: { backgroundColor: "#FEF9E7", textColor: "#B7950B" },
    CrossDocked: { backgroundColor: "#E8DAEF", textColor: "#8E44AD" },
    SendOut: { backgroundColor: "#FADBD8", textColor: "#E74C3C" },
    Delivered: { backgroundColor: "#DFF7DF", textColor: "green" },
    DeliveryFailed: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
    Deconsolidation: { backgroundColor: "#E8ECEF", textColor: "#566573" }, // Added for Deconsolidation status
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const NoPriceShipmentListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";

  const { isVendor, isCustomer, viewEntity } = useAuth();

  const [status, setStatus] = React.useState("");
  const [sourceBranch, setSourceBranch] = React.useState("");

  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "No Price Shipment Reports",
    },
  ];
  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      cell: (row) => row.shipmentId,
    },
    {
      name: "Sender",
      width: "220px",
      selector: (row) => row?.customer?.name || row?.vendor?.ownerName || "N/A",
      cell: (row) => (
        <Box
          sx={{
            maxWidth: "600px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row?.customer?.name
            ? `CUS: ${row.customer.name}`
            : row?.vendor?.ownerName
            ? `VEN: ${row.vendor.ownerName}`
            : "N/A"}
        </Box>
      ),
      omit: isVendor || isCustomer,
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.name,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.name || "N/A"}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.receiver?.phone || ""}
          </span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name || "N/A",
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name || "N/A",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Box display="flex">
          <Chip
            label={row?.status?.toUpperCase() || "N/A"}
            variant="outlined"
            sx={{
              backgroundColor: getStatusStyle(row?.status).backgroundColor,
              color: getStatusStyle(row?.status).textColor,
              fontWeight: "bold",
              height: "17px",
              borderRadius: "12px",
              width: "auto",
              padding: "0",
              border: "none",
              "& .MuiChip-label": {
                fontSize: "0.6rem",
                padding: "3px 5px",
                lineHeight: "14px",
              },
            }}
          />
        </Box>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Link to={`/app/crm/shipment/${row?._id}/detail`}>
            <IconButton size="small" aria-label="View Shipment" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetUniqueDestinationBranchesQuery();

  const [
    fetchNoPriceShipments,
    { data: shipmentRes, isLoading, isFetching, error },
  ] = useLazyNoPriceShipmentListQuery();

  const branchOptions = React.useMemo(() => {
    const validBranches = (branches?.data || [])
      .filter((branch) => branch._id && branch.name)
      .filter(
        (branch, index, self) =>
          index === self.findIndex((b) => b._id === branch._id)
      );
    return [{ label: "All Branches", value: "" }, ...validBranches];
  }, [branches?.data]);

  React.useEffect(() => {
    fetchBranches({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      status: status || undefined,
      ...(keyword && { keyword }),
      ...(!status && { excludeDelivered: true }),
    });
  }, [fetchBranches, viewEntity, keyword, status]);

  React.useEffect(() => {
    fetchNoPriceShipments({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(sourceBranch && { sourceBranch }),
      ...(status && { status }),
    });
  }, [
    fetchNoPriceShipments,
    currentPage,
    perPage,
    keyword,
    sourceBranch,
    status,
  ]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <Box display="flex" justifyContent="space-between" mt={1}>
          <TableFilter
            searchLabel="Search by Shipment ID"
            disabled={isLoading || isFetching}
          >
            <BaseSelect
              fullWidth
              label="Source Branch"
              value={sourceBranch}
              onChange={(e) => setSourceBranch(e.target.value)}
              options={branchOptions}
              getOptionLabel={(opt) =>
                opt.name || opt.label || "Unknown Branch"
              }
              getOptionValue={(opt) => opt._id || opt.value || ""}
              disabled={loadingBranches}
            />

            <BaseSelect
              fullWidth
              label="By Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={[
                { label: "All Statuses", value: "" },
                { label: "Pending", value: "Pending" },
                { label: "Collected", value: "Collected" },
                { label: "Loaded", value: "Loaded" },
                { label: "Unloaded", value: "Unloaded" },
                { label: "CrossDocked", value: "CrossDocked" },
                { label: "SendOut", value: "SendOut" },
                { label: "Warehousing", value: "Warehousing" },
                { label: "DeliveryFailed", value: "DeliveryFailed" },
                { label: "Return", value: "Return" },
                { label: "Cancelled", value: "Cancelled" },
              ]}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              minWidth="250px"
            />
          </TableFilter>
          <ExportNoPriceShipmentExcel />
        </Box>
        <Divider />

        <AppTable
          columns={columns}
          data={shipmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default NoPriceShipmentListScreen;
